import { AddOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Fade,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { t } from "i18next";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  IDailyContentPlaylist,
  NavigationLevel,
  ScheduleContentRefType,
} from "../../../../types/NendaTypes";
import ConfirmationDialog from "../ConfirmationDialog";
import CreateEditPlaylist, {
  contentTypes,
} from "../components/DailyPlaylist/CreateEditPlaylist";
import PlaylistsTable from "../components/DailyPlaylist/PlaylistsTable";
import { isAllTimeIntervalsValidWithoutOverlaps } from "../components/DailyPlaylist/helpers/validation";
import { store } from "../store";
import {
  createPlaylist,
  deletePlaylist,
  getPlaylists,
  selectCreateEditPlaylistModalIsOpen,
  selectDeletePlaylist,
  selectEditPlaylist,
  selectPlaylistsByScope,
  setCreateEditPlaylistModalIsOpen,
  setDeletePlaylist,
  setEditPlaylist,
  updatePlaylist,
} from "../store/reducers/dailyContentPlaylistReducer";
import {
  selectCurrentNavigationLevel,
  selectNavigationScope,
} from "../store/reducers/workspaceReducer";
import DefaultDialog from "../ui-components/dialog/dialog";
import { selectContentChannelsByScope } from "../store/reducers/contentChannelReducer";
import {
  getCompanySignagePlaylists,
  getPremiseSignagePlaylists,
  selectSignagePlaylistsByNavigationLevel,
} from "../store/reducers/signagePlaylistReducer";
import HelpWidget from "../ui-components/display/HelpWidget";
import { selectNavigatedPremise } from "../store/reducers/organizationUnitReducer";

const DailyPlaylists: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const scope = useSelector(selectNavigationScope);
  const navigationLevel = useSelector(selectCurrentNavigationLevel);

  const playlists = useSelector(selectPlaylistsByScope);

  const playListsForScope =
    navigationLevel === NavigationLevel.COMPANY
      ? playlists.companyPlaylists
      : navigationLevel === NavigationLevel.PREMISE
        ? playlists.premisePlaylists
        : [];

  const { dispatch } = store;
  const createModalIsOpen = useSelector(selectCreateEditPlaylistModalIsOpen);
  const editPlaylistId = useSelector(selectEditPlaylist);
  const selectedPlaylistToEdit = playListsForScope.find(
    (p) => p._id === editPlaylistId
  );
  const deletePlaylistId = useSelector(selectDeletePlaylist);
  const premise = useSelector(selectNavigatedPremise);

  const initPlaylist = useMemo(
    () => ({
      name: "",
      premise: scope.premise,
      company: scope.company,
      timeline: [],
    }),
    [scope.premise, scope.company]
  );

  useEffect(() => {
    setPlaylist((prev) => ({
      ...prev,
      premise: scope.premise,
      company: scope.company,
    }));
  }, [scope.premise, scope.company]);

  const [playlist, setPlaylist] = useState<IDailyContentPlaylist>(initPlaylist);

  useEffect(() => {
    if (playlist._id !== selectedPlaylistToEdit?._id) {
      setPlaylist(selectedPlaylistToEdit || initPlaylist);
    }
  }, [selectedPlaylistToEdit, playlist._id, initPlaylist]);

  const playlistCreateEditIsValid = useMemo(() => {
    const isValid =
      !!playlist.name && !!playlist.company && !!playlist.timeline;
    return isValid;
  }, [playlist]);

  const handleCreateEditPlaylistModalClose = ({
    isConfirmed,
  }: {
    isConfirmed: boolean;
  }) => {
    if (
      navigationLevel !== NavigationLevel.PREMISE &&
      navigationLevel !== NavigationLevel.COMPANY
    )
      return;

    const parentId =
      navigationLevel === NavigationLevel.COMPANY
        ? scope.company
        : scope.premise;

    if (!parentId) return;

    if (isConfirmed && playlistCreateEditIsValid) {
      if (selectedPlaylistToEdit?._id)
        dispatch(
          updatePlaylist({
            scope: navigationLevel,
            parentId: parentId,
            playlist: { ...playlist, _id: selectedPlaylistToEdit?._id },
          })
        );
      if (!selectedPlaylistToEdit?._id)
        dispatch(
          createPlaylist({
            scope: navigationLevel,
            parentId: parentId,
            playlist: { ...playlist },
          })
        );
    }
    dispatch(setCreateEditPlaylistModalIsOpen(false));
    dispatch(setEditPlaylist(null));
    setPlaylist(initPlaylist);
    return;
  };

  const handleCreatePlaylistModalOpen = () => {
    dispatch(setCreateEditPlaylistModalIsOpen(true));
  };

  const handleDeletePlaylist = (isConfirmed: boolean) => {
    if (
      navigationLevel !== NavigationLevel.PREMISE &&
      navigationLevel !== NavigationLevel.COMPANY
    )
      return;

    if (!deletePlaylistId) return;

    const parentId =
      navigationLevel === NavigationLevel.COMPANY
        ? scope.company
        : scope.premise;

    if (!parentId) return;

    if (isConfirmed) {
      dispatch(
        deletePlaylist({
          scope: navigationLevel,
          parentId: parentId,
          playlistId: deletePlaylistId,
        })
      );
    } else {
      dispatch(setDeletePlaylist(null));
    }
  };

  useEffect(() => {
    if (navigationLevel === NavigationLevel.COMPANY) {
      if (scope.company)
        dispatch(
          getPlaylists({
            scope: NavigationLevel.COMPANY,
            parentId: scope.company,
          })
        );
    }
    if (navigationLevel === NavigationLevel.PREMISE) {
      if (scope.premise)
        dispatch(
          getPlaylists({
            scope: NavigationLevel.PREMISE,
            parentId: scope.premise,
          })
        );
    }
  }, [scope.premise, scope.company, dispatch, navigationLevel]);

  const playlistIsValid = useMemo(() => {
    if (!playlist.timeline) return false;

    return (
      isAllTimeIntervalsValidWithoutOverlaps(
        playlist.timeline.map((t) => t.interval)
      ) && playlistCreateEditIsValid
    );
  }, [playlist.timeline, playlistCreateEditIsValid]);
  const signagePlaylists = useSelector(selectSignagePlaylistsByNavigationLevel);
  const contentChannels = useSelector(selectContentChannelsByScope);

  useEffect(() => {
    if (scope.company) {
      store.dispatch(getCompanySignagePlaylists(scope.company));
    }
    if (scope.premise) {
      store.dispatch(getPremiseSignagePlaylists(scope.premise));
    }
  }, [scope.company, scope.premise]);

  const activeCategories = useMemo(() => {
    const categories = contentTypes;
    if (scope.company) return categories;
    if (premise?.signageConfig?.isSignageAsContentEnabled) return categories;
    return categories.filter(
      (category) => category.value !== ScheduleContentRefType.SIGNAGE_PLAYLIST
    );
  }, [premise]);

  return (
    <Fade in={true}>
      <Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Stack
              direction={"row"}
              gap="2rem"
              alignItems={"center"}
              mb="2rem"
              sx={(theme) => ({
                [theme.breakpoints.down("md")]: {
                  flexDirection: "column",
                  gap: "1rem",
                },
              })}
            >
              <Typography
                variant="h3"
                sx={(theme) => ({
                  textTransform: "capitalize",
                  color: theme.palette.text.primary,
                })}
              >
                {t("customerportal.pages.daily_playlists.title")}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddOutlined />}
                onClick={handleCreatePlaylistModalOpen}
              >
                {t("customerportal.pages.daily_playlists.create_playlist")}
              </Button>
            </Stack>
            <Typography variant="body1" maxWidth={"30rem"}>
              {t("customerportal.pages.daily_playlists.helper_text")}
            </Typography>
            <DefaultDialog
              width="80vw"
              open={createModalIsOpen || !!editPlaylistId}
              disabled={!playlistIsValid}
              onClose={handleCreateEditPlaylistModalClose}
              title={
                editPlaylistId
                  ? (t(
                      "customerportal.pages.daily_playlists.edit_playlist"
                    ) as string)
                  : (t(
                      "customerportal.pages.daily_playlists.create_new_playlist"
                    ) as string)
              }
            >
              <CreateEditPlaylist
                playlistState={playlist}
                setPlaylistState={setPlaylist}
                contentChannels={contentChannels}
                signagePlaylists={signagePlaylists}
                contentCategories={activeCategories}
              />
            </DefaultDialog>
            <ConfirmationDialog
              title={t(
                "customerportal.pages.daily_playlists.delete_confirmation.title"
              )}
              isOpen={!!deletePlaylistId}
              onClose={handleDeletePlaylist}
              content={t(
                "customerportal.pages.daily_playlists.delete_confirmation.description"
              )}
            />
          </Box>
          <Box>
            <HelpWidget
              title="Help"
              details={
                <Box>
                  <Typography variant="body2">
                    <strong>
                      {t("customerportal.pages.daily_playlists.help.title")}
                    </strong>
                  </Typography>
                  <Typography variant="body1">
                    {t("customerportal.pages.daily_playlists.help.section_one")}
                  </Typography>
                  <Typography variant="body1">
                    {t("customerportal.pages.daily_playlists.help.section_two")}{" "}
                    <Link
                      href={`/scheduling/schedule/premise/${scope.premise}`}
                    >
                      {t("customerportal.pages.daily_playlists.help.link")}
                    </Link>
                  </Typography>
                </Box>
              }
            />
          </Box>
          {/* Modal */}
        </Box>
        {/* Main */}
        <PlaylistsTable dailyPlaylists={playListsForScope} />
      </Box>
    </Fade>
  );
};

export default DailyPlaylists;
